import { createAction, props } from '@ngrx/store';
import type { SessionTimeoutConfig } from './session-timeout.config';

// Authentication lifecycle
export const initiateAuth = createAction('[App Lifecycles] Initiate Auth');
export const preAuthTasksCompleted = createAction(
  '[App Lifecycles] Pre-Auth Tasks Completed'
);
export const authCompleted = createAction('[App Lifecycles] Auth Completed');
export const postAuthTasksCompleted = createAction(
  '[App Lifecycles] Post-Auth Tasks Completed'
);

// Authorization lifecycle
export const initiateAuthorization = createAction(
  '[App Lifecycles] Initiate Authorization'
);
export const preAuthorizationTasksCompleted = createAction(
  '[App Lifecycles] Pre-Authorization Tasks Completed'
);
export const authorizationCompleted = createAction(
  '[App Lifecycles] Authorization Completed'
);
export const postAuthorizationTasksCompleted = createAction(
  '[App Lifecycles] Post-Authorization Tasks Completed'
);

// Logout lifecycle
export const initiateLogout = createAction('[App Lifecycles] Initiate Logout');
export const preLogoutTasksCompleted = createAction(
  '[App Lifecycles] Pre-Logout Tasks Completed'
);
export const logoutCompleted = createAction(
  '[App Lifecycles] Logout Completed'
);
export const postLogoutTasksCompleted = createAction(
  '[App Lifecycles] Post-Logout Tasks Completed'
);
export const logoutCancelled = createAction(
  '[App Lifecycles] Logout Cancelled'
);

// Session inactivity lifecycle (EDUS-2655)
/** Authenticated — begin tracking. Seeds the clock and the configured timings. */
export const sessionTrackingStarted = createAction(
  '[App Lifecycles] Session Tracking Started',
  props<{ atMs: number; config: SessionTimeoutConfig }>()
);
export const sessionTrackingStopped = createAction(
  '[App Lifecycles] Session Tracking Stopped'
);
/**
 * User did something. Dispatched identically whether the trigger was a local
 * DOM event or a peer tab's `storage` event — one action, two sources.
 */
export const activityDetected = createAction(
  '[App Lifecycles] Activity Detected',
  props<{ atMs: number }>()
);
/** Advances the store's notion of "now" so the derivations can be re-evaluated. */
export const sessionTick = createAction(
  '[App Lifecycles] Session Tick',
  props<{ nowMs: number }>()
);
/** Explicit "Stay Logged In". Unlike activityDetected, this extends mid-warning. */
export const sessionExtended = createAction(
  '[App Lifecycles] Session Extended',
  props<{ atMs: number }>()
);
export const sessionExpired = createAction('[App Lifecycles] Session Expired');
/**
 * Secondary button on the warning modal. Terminates the session immediately
 * rather than waiting out the countdown. Kept distinct from `sessionExpired`
 * so the audit entry can tell a deliberate exit from a timeout (EDUS-2657).
 */
export const sessionLogoutRequested = createAction(
  '[App Lifecycles] Session Logout Requested'
);

/**
 * Another tab extended the session (AC 3.3). Distinct from `sessionExtended`
 * so the effect that broadcasts to peers can ignore it — otherwise two tabs
 * would echo the same extension back and forth.
 */
export const peerSessionExtended = createAction(
  '[App Lifecycles] Peer Session Extended',
  props<{ atMs: number }>()
);
/**
 * Another tab ended the session (AC 3.2). This tab leaves the page but does
 * not sign out or write an audit entry — the originating tab already did.
 */
export const peerSessionTerminated = createAction(
  '[App Lifecycles] Peer Session Terminated'
);

// Failure action
export const lifecycleHookFailed = createAction(
  '[App Lifecycles] Lifecycle Hook Failed',
  props<{ phase: string; error: unknown }>()
);
